/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import { screen } from "@monsma/XDL/Helpers";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    supportServices = "supportServices",
    orderingWithMonsma = "orderingWithMonsma",
}

interface SupportOrderingProps extends WidgetProps {
    fields: {
        [fields.supportServices]: string;
        [fields.orderingWithMonsma]: string;
    };
}

export interface SupportOrderingStyles {
    container?: GridContainerProps;
    supportServicesGridItem?: GridItemProps;
    orderingWithMonsmaGridItem?: GridItemProps;
}

export const supportOrderingStyles: SupportOrderingStyles = {
    container: {
        gap: 0,
    },
    supportServicesGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;

            ${screen("gte", "md")} {
                padding-right: 50px;
            }
        `,
    },
    orderingWithMonsmaGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;

            ul {
                margin: 0;
                padding: 0 0 0 18px;

                li {
                    margin-bottom: 15px;

                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }

            ${screen("gte", "md")} {
                padding-right: 45px;
            }
        `,
    },
};

const styles = supportOrderingStyles;

const SupportOrderingWidget: React.FC<SupportOrderingProps> = props => {
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.supportServicesGridItem}>
                {parse(props.fields.supportServices, parserOptions)}
            </GridItem>
            <GridItem {...styles.orderingWithMonsmaGridItem}>
                {parse(props.fields.orderingWithMonsma, parserOptions)}
            </GridItem>
        </GridContainer>
    );
};

export const definition: WidgetDefinition = {
    displayName: "Support Services / Ordering with Monsma",
    group: "Product Details",
    allowedContexts: [ProductDetailsPageContext],
    fieldDefinitions: [
        {
            name: fields.supportServices,
            displayName: "Support Services",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.orderingWithMonsma,
            displayName: "Ordering with Monsma",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: SupportOrderingWidget,
    definition,
};

export default widgetModule;
